import { useEffect, useState } from "react";
import { Testimonial, BeforeAfterContent } from "../../services/types";

interface ContentCarouselProps {
    testimonials: Testimonial[];
    beforeAfterContent: BeforeAfterContent[];
}

const ContentCarousel = ({ testimonials, beforeAfterContent }: ContentCarouselProps) => {
    const combinedContent = [...beforeAfterContent, ...testimonials]; // Merging both arrays
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        // Preload all images for better performance
        beforeAfterContent.forEach((item) => {
            const beforeImg = new Image();
            beforeImg.src = item.before_url;

            const afterImg = new Image();
            afterImg.src = item.after_url;
        });

        testimonials.forEach((item) => {
            const testimonialImg = new Image();
            testimonialImg.src = item.url;
        });
    }, [beforeAfterContent, testimonials]);

    const changeItem = (newIndex: number) => {
        setCurrentIndex(newIndex);
    };

    const nextItem = () => {
        changeItem((currentIndex + 1) % combinedContent.length);
    };

    const prevItem = () => {
        changeItem((currentIndex - 1 + combinedContent.length) % combinedContent.length);
    };

    const isTestimonial = (item: unknown): item is Testimonial =>
        !!item && typeof item === "object" && "description" in item;

    const currentItem = combinedContent[currentIndex];

    return (
        <div className="mt-4 relative border-2 rounded-md border-memphis-blue p-4 flex flex-col justify-center min-h-[400px]">
            {/* Left Arrow */}
            <button
                className={`absolute left-4 top-1/2 transform -translate-y-1/2 
                    px-3 py-[5px] rounded-full shadow-md 
                    ${combinedContent.length <= 1 ? "bg-gray-400 cursor-not-allowed" : "bg-memphis-blue hover:bg-blue-500"}`}
                onClick={prevItem}
                disabled={combinedContent.length <= 1}
            >
                <p className="text-white text-2xl mb-1">&#8592;</p>
            </button>

            {/* Content with Fade Transition */}
            <div className="grid place-items-center h-full transition-opacity duration-300" key={currentIndex}>
                {isTestimonial(currentItem) ? (
                    // Testimonial View
                    <div className="grid grid-cols-2 gap-4 items-center pl-16 pr-6 h-full">
                        <img 
                            src={currentItem.url} 
                            alt="Testimonial"
                            loading="lazy" 
                            className="rounded-md shadow-md object-contain aspect-[4/3] w-[350px]"
                        />
                        <div className="space-y-4 pl-4 pr-16 flex flex-col justify-center h-full">
                            <p className="text-memphis-blue line-clamp-8">{`"${currentItem.description}"`}</p>
                        </div>
                    </div>
                ) : (
                    // Before/After Image View
                    <div className="grid grid-cols-2 gap-12 items-center h-full">
                        <div className="aspect-[4/3] w-full max-w-[350px] flex flex-col items-center justify-center">
                            <img 
                                src={currentItem?.before_url} 
                                alt="Before Image"
                                loading="lazy"
                                className="w-full h-full object-contain rounded-md shadow-md"
                            />
                            <p className="text-center font-semibold mt-2">BEFORE</p>
                        </div>
                        <div className="aspect-[4/3] w-full max-w-[350px] flex flex-col items-center justify-center">
                            <img 
                                src={currentItem?.after_url} 
                                alt="After Image"
                                loading="lazy"
                                className="w-full h-full object-contain rounded-md shadow-md"
                            />
                            <p className="text-center font-semibold mt-2">AFTER</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Right Arrow */}
            <button
                className={`absolute right-4 top-1/2 transform -translate-y-1/2 
                    px-3 py-[5px] rounded-full shadow-md 
                    ${combinedContent.length <= 1 ? "bg-gray-400 cursor-not-allowed" : "bg-memphis-blue hover:bg-blue-500"}`}
                onClick={nextItem}
                disabled={combinedContent.length <= 1}
            >
                <p className="text-white text-2xl mb-1">&#8594;</p>
            </button>
        </div>
    );
};

export default ContentCarousel;